/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

/*============================================================
  MATERIAL UI
============================================================*/
import Grid from "@mui/material/Grid2";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import TextareaAutosize from "@mui/material/TextareaAutosize";

/*============================================================
  PROPRIETARY - COMPONENTS
============================================================*/
import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

/*============================================================
  PROPRIETARY - REDUX DATA SLICES
============================================================*/
import { updateDisability } from "../../../states/slcObjectives.js";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import {
  valudateTextfield2,
  validateCurrencyField2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { pvAnnuity } from "../../../utils/formula.js";

export default function Disability() {
  const disability = useSelector((state) => state.objectives.protection.disability);

  const [disMonthlyAmt, setDisMonthlyAmt] = useState(disability.disMonthlyAmt);

  const [disAnnualAmt, setDisAnnualAmt] = useState(disability.disAnnualAmt);
  const [disYearsRequired, setDisYearsRequired] = useState(disability.disYearsRequired);
  const [disPctReturnRate, setDisPctReturnRate] = useState(disability.disPctReturnRate);
  const [disPctInfRate, setDisPctInfRate] = useState(disability.disPctInfRate);
  const [disPctInfAdjRate, setDisPctInfAdjRate] = useState(disability.disPctInfAdjRate);
  const [disFundsRequired, setDisFundsRequired] = useState(disability.disFundsRequired);

  const [disMedicalExpAmt, setDisMedicalExpAmt] = useState(disability.disMedicalExpAmt);
  const [disOtherDesc, setDisOtherDesc] = useState(disability.disOtherDesc);
  const [disOtherDescError, setDisOtherDescError] = useState();
  const [disOtherAmt, setDisOtherAmt] = useState(disability.disOtherAmt);
  const [disTotalExpenses, setDisTotalExpenses] = useState(disability.disTotalExpenses);

  const [disResTpd, setDisResTpd] = useState(disability.disResTpd);
  const [disResOtherDesc, setDisResOtherDesc] = useState(disability.disResOtherDesc);
  const [disResOtherDescError, setDisResOtherDescError] = useState();
  const [disResOtherAmt, setDisResOtherAmt] = useState(disability.disResOtherAmt);
  const [disResTotalResources, setDisResTotalResources] = useState(disability.disResTotalResources);

  const [disTotalRequiredFunds, setDisTotalRequiredFunds] = useState(disability.disTotalRequiredFunds);

  const [disSurplusShortfall, setDisSurplusShortfall] = useState(disability.disSurplusShortfall);

  const [deviationReason, setDeviationReason] = useState(disability.deviationReason);
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcTotalIncomeRequired, [disAnnualAmt, disYearsRequired, disPctInfAdjRate]);
  useEffect(calcTotalExpensesRequired, [disMedicalExpAmt, disOtherAmt]);
  useEffect(calcTotalFundsRequired, [disFundsRequired, disTotalExpenses]);
  useEffect(calcTotalResources, [disResTpd, disResOtherAmt]);
  useEffect(calcSurplusShortfall, [disResTotalResources, disTotalRequiredFunds]);

  /**==============================
   * FUNCTIONS
   ==============================*/
  function calcTotalIncomeRequired() {
    //CALCULATE PV
    let presentValue = pvAnnuity(
      Number(removeCommaFromCurrency(disAnnualAmt)),
      Number(disPctInfAdjRate) / 100,
      Number(disYearsRequired),
      1
    ).toFixed(2);

    updateRedux("disFundsRequired", presentValue, updateDisability);
    setDisFundsRequired(presentValue);
  }

  function calcTotalExpensesRequired() {
    let totalExpenses =
      Number(removeCommaFromCurrency(disMedicalExpAmt)) + Number(removeCommaFromCurrency(disOtherAmt));

    totalExpenses = convertLocaleString(totalExpenses, "en-US", 2);

    updateRedux("disTotalExpenses", totalExpenses, updateDisability);
    setDisTotalExpenses(totalExpenses);
  }

  function calcTotalFundsRequired() {
    let totalFundsRequired =
      Number(removeCommaFromCurrency(disFundsRequired)) + Number(removeCommaFromCurrency(disTotalExpenses));

    totalFundsRequired = convertLocaleString(totalFundsRequired, "en-US", 2);

    updateRedux("disTotalRequiredFunds", totalFundsRequired, updateDisability);
    setDisTotalRequiredFunds(totalFundsRequired);
  }

  function calcTotalResources() {
    let totalResources = Number(removeCommaFromCurrency(disResTpd)) + Number(removeCommaFromCurrency(disResOtherAmt));

    totalResources = convertLocaleString(totalResources, "en-US", 2);

    updateRedux("disResTotalResources", totalResources, updateDisability);
    setDisResTotalResources(totalResources);
  }

  function calcSurplusShortfall() {
    let totalSurplusShortfall =
      Number(removeCommaFromCurrency(disResTotalResources)) - Number(removeCommaFromCurrency(disTotalRequiredFunds));

    totalSurplusShortfall = convertLocaleString(totalSurplusShortfall, "en-US", 2);

    updateRedux("disSurplusShortfall", totalSurplusShortfall, updateDisability);
    setDisSurplusShortfall(totalSurplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    //DISABILITY - INCOME
    if (id === "disMonthlyAmt") {
      input_data.fieldName = "Monthly income";
      input_data.fieldVariable = disMonthlyAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        //Annual Amount - Calculation
        let annualAmount = Number(result.value.replace(/,/g, "")) * 12;

        annualAmount = convertLocaleString(annualAmount, "en-US", 2);

        updateRedux("disMonthlyAmt", result.value, updateDisability);
        updateRedux("disAnnualAmt", annualAmount, updateDisability);
        setDisMonthlyAmt(result.value);
        setDisAnnualAmt(annualAmount);
      }
    }

    if (id === "disYearsRequired") {
      input_data.fieldName = "Years";
      input_data.fieldVariable = disYearsRequired;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setDisYearsRequired(result.value);
        updateRedux("disYearsRequired", result.value, updateDisability);
      }
    }

    if (id === "disPctReturnRate") {
      input_data.fieldName = "Rate of return (%)";
      input_data.fieldVariable = disPctReturnRate;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setDisPctReturnRate(result.value);
        updateRedux("disPctReturnRate", result.value, updateDisability);
      }
    }

    if (id === "disPctInfRate") {
      input_data.fieldName = "Assumed inflation rate (%)";
      input_data.fieldVariable = disPctInfRate;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setDisPctInfRate(result.value);
        updateRedux("disPctInfRate", result.value, updateDisability);
      }
    }

    if (id === "disPctInfAdjRate") {
      input_data.fieldName = "Assumed inflation rate (%)";
      input_data.fieldVariable = disPctInfAdjRate;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setDisPctInfAdjRate(result.value);
        updateRedux("disPctInfAdjRate", result.value, updateDisability);
      }
    }

    if (id === "disFundsRequired") {
      input_data.fieldName = "Income funds required";
      input_data.fieldVariable = disFundsRequired;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setDisFundsRequired(result.value);
        updateRedux("disFundsRequired", result.value, updateDisability);
      }
    }

    //DISABILITY - EXPENSES
    if (id === "disMedicalExpAmt") {
      input_data.fieldName = "Medical expenses";
      input_data.fieldVariable = disMedicalExpAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setDisMedicalExpAmt(result.value);
        updateRedux("disMedicalExpAmt", result.value, updateDisability);
      }
    }

    if (id === "disOtherDesc") {
      input_data.fieldName = "Other expenses";
      input_data.fieldVariable = disOtherDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDisOtherDesc(result.value);
        updateRedux("disOtherDesc", result.value, updateDisability);
      }

      setDisOtherDescError(result.errorMessage);
    }

    if (id === "disOtherAmt") {
      input_data.fieldName = "Other amount";
      input_data.fieldVariable = disOtherAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setDisOtherAmt(result.value);
        updateRedux("disOtherAmt", result.value, updateDisability);
      }
    }

    //DISABILITY - ALLOCATED RESOURCES
    if (id === "disResTpd") {
      input_data.fieldName = "Income funds required";
      input_data.fieldVariable = disResTpd;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setDisResTpd(result.value);
        updateRedux("disResTpd", result.value, updateDisability);
      }
    }

    if (id === "disResOtherDesc") {
      input_data.fieldName = "Other resource allocated";
      input_data.fieldVariable = disResOtherDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDisResOtherDesc(result.value);
        updateRedux("disResOtherDesc", result.value, updateDisability);
      }

      setDisResOtherDescError(result.errorMessage);
    }

    if (id === "disResOtherAmt") {
      input_data.fieldName = "Other amount";
      input_data.fieldVariable = disResOtherAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setDisResOtherAmt(result.value);
        updateRedux("disResOtherAmt", result.value, updateDisability);
      }
    }

    //CRITICAL ILLNESS - SURPLUS AND SHORTFALL
    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateDisability);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={2} size={12}>
        <GridSectionHeader
          sectionName="In the Event of Disability"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            backgroundColor: "#10B981",
          }}
        />

        {/*==============================
        INCOME
        ==============================*/}
        <Grid size={12}>
          <Typography variant="h5" sx={{ fontWeight: "bold", textDecoration: "underline" }}>
            Income
          </Typography>
        </Grid>

        {/*==============================
        MONTHLY AMOUNT NEEDS
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Monthly amount needs for living expenses and/or dependants
          </Typography>
        </Grid>
        <GridTextfield
          id="disMonthlyAmt"
          key="disMonthlyAmt"
          columnSize="3"
          label="Monthly"
          isCurrency={true}
          currState={disMonthlyAmt}
          onChange={handleChange}
        />
      </Grid>

      {/*==============================
        ANNUAL AMOUNT NEEDS
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Annual Amount Needs for Living Expenses and/or Dependants (pmt)
        </Typography>
      </Grid>

      <GridTextfield
        id="disAnnualAmt"
        key="disAnnualAmt"
        columnSize="3"
        label="Annually"
        isCurrency={true}
        currState={disAnnualAmt}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        NUMBER OF YEARS REQUIRED
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Number of Years Required (n)
        </Typography>
      </Grid>

      <GridTextfield
        id="disYearsRequired"
        key="disYearsRequired"
        columnSize="3"
        label="Years"
        currState={disYearsRequired}
        onChange={handleChange}
      />

      {/*==============================
        INVESTMENT RATE OF RETURN
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Investment Rate of Return (%) (R)
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>

      <GridTextfield
        id="disPctReturnRate"
        key="disPctReturnRate"
        columnSize="3"
        label="Percent"
        currState={disPctReturnRate}
        onChange={handleChange}
      />

      {/*==============================
        ASSUMED INFLATION RATE
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Assumed Inflation Rate. (%) (i)
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>

      <GridTextfield
        id="disPctInfRate"
        key="disPctInfRate"
        columnSize="3"
        label="Percent"
        currState={disPctInfRate}
        onChange={handleChange}
      />

      {/*==============================
        INFLATION ADJUSTED RATE OF RETURN FROM INVESTMENT
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Inflation-adjusted Rate of Return from investments(%) (i)
        </Typography>
      </Grid>

      <GridTextfield
        id="disPctInfAdjRate"
        key="disPctInfAdjRate"
        columnSize="3"
        label="Percent"
        currState={disPctInfAdjRate}
        onChange={handleChange}
      />

      {/*==============================
        FUND REQUIRED TO PROVIDE INCOME
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (A) Funds Required to Provide Income (pv)
        </Typography>
      </Grid>

      <GridTextfield
        id="disFundsRequired"
        key="disFundsRequired"
        columnSize="3"
        isCurrency={true}
        currState={disFundsRequired}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        EXPENSES
        ==============================*/}
      <Grid size={12}>
        <Typography
          variant="h5"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
            textDecoration: "underline",
          }}
        >
          Expenses
        </Typography>
      </Grid>

      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Provision for Medical Expenses
        </Typography>
      </Grid>

      <GridTextfield
        id="disMedicalExpAmt"
        key="disMedicalExpAmt"
        columnSize="3"
        isCurrency={true}
        currState={disMedicalExpAmt}
        onChange={handleChange}
      />

      {/*==============================
        EXPENSES - OTHERS
        ==============================*/}
      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>

      <GridTextfield
        id="disOtherDesc"
        key="disOtherDesc"
        columnSize="8"
        currState={disOtherDesc}
        onChange={handleChange}
        error={disOtherDescError}
        helperText={disOtherDescError}
      />
      <GridTextfield
        id="disOtherAmt"
        key="disOtherAmt"
        columnSize="3"
        isCurrency={true}
        currState={disOtherAmt}
        onChange={handleChange}
      />

      {/*==============================
        TOTAL EXPENSES
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (B) Total Expenses
        </Typography>
      </Grid>

      <GridTextfield
        id="disTotalExpenses"
        key="disTotalExpenses"
        columnSize="3"
        isCurrency={true}
        currState={disTotalExpenses}
        onChange={handleChange}
        disabled={true}
      />

      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (C) Total Funds Required = (A + B)
        </Typography>
      </Grid>

      <GridTextfield
        id="disTotalRequiredFunds"
        key="disTotalRequiredFunds"
        columnSize="3"
        isCurrency={true}
        currState={disTotalRequiredFunds}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        EXISTING RESOURCES ALLOCATED
      ==============================*/}
      <Grid size={12}>
        <Typography
          variant="h5"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
            textDecoration: "underline",
          }}
        >
          Existing Resources Allocated (Current Values)
        </Typography>
      </Grid>

      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Existing Total Permanent Disability (TPD) payout
        </Typography>
      </Grid>

      <GridTextfield
        id="disResTpd"
        key="disResTpd"
        columnSize="3"
        isCurrency={true}
        currState={disResTpd}
        onChange={handleChange}
      />

      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>

      <GridTextfield
        id="disResOtherDesc"
        key="disResOtherDesc"
        columnSize="8"
        currState={disResOtherDesc}
        onChange={handleChange}
        error={disResOtherDescError}
        helperText={disResOtherDescError}
      />

      <GridTextfield
        id="disResOtherAmt"
        key="disResOtherAmt"
        columnSize="3"
        isCurrency={true}
        currState={disResOtherAmt}
        onChange={handleChange}
      />

      {/*==============================
        TOTAL FUNDS AVAILABLE
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (D) Total Resources Available
        </Typography>
      </Grid>

      <GridTextfield
        id="disResTotalResources"
        key="disResTotalResources"
        columnSize="3"
        isCurrency={true}
        currState={disResTotalResources}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        SURPLUS / SHORTFALL
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (D - C) = Surplus / Shortfall
        </Typography>
      </Grid>

      <GridTextfield
        id="disSurplusShortfall"
        key="disSurplusShortfall"
        columnSize="3"
        isCurrency={true}
        currState={disSurplusShortfall}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        REASON FOR DEVIATION
      ==============================*/}
      <Grid size={12}>
        <Typography variant="body1">
          <b>Reason(s) for Deviations:</b>
        </Typography>

        <TextareaAutosize
          id="deviationReason"
          placeholder="Type here..."
          style={{ width: "100%" }}
          minRows={5}
          onChange={handleChange}
          defaultValue={deviationReason}
        />
        {deviationReasonError && <FormHelperText error={deviationReasonError}>{deviationReasonError}</FormHelperText>}
      </Grid>
    </>
  );
}
